import type { ReactNode } from 'react'
import { AlertTriangle, HelpCircle } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from '@/components/ui/Dialog'
import { Button } from '@/components/ui/Button'
import { Text } from '@/components/ui/Text'
import { cn } from '@/lib/cn'

/**
 * ConfirmDialog — one confirmation dialog, for any irreversible action.
 *
 * Named `ConfirmDialog` until 2026-09-17, and the name was the bug. It was never
 * delete-only — it has had a `tone` prop for "confirmations that build rather than destroy"
 * (approve, activate, restore) for some time — but four callers hand-rolled their own `<Dialog>`
 * for cancel / approve / purge / bulk-apply, because nobody looking for a way to confirm a
 * non-delete finds something called ConfirmDialog. Renamed with no alias kept: two names
 * for one component is the same defect as two names for one colour.
 */
export interface ConfirmDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  description?: string
  onConfirm: () => void
  isLoading?: boolean
  /** Label for the confirm button (default: "Delete") */
  confirmLabel?: string
  /**
   * Label for the dismiss button (default: "Cancel").
   *
   * Needed the moment the CONFIRM action is itself called "Cancel" — `CancelInstanceDialog`
   * hand-rolled this whole component rather than ship a dialog whose two buttons both said
   * Cancel. Its wording ("Keep Document") is the right instinct and now has somewhere to live.
   */
  cancelLabel?: string
  /** Inline error banner shown inside the dialog (dialog stays open when set) */
  errorBanner?: string
  /**
   * Severity of the action being confirmed. Drives the icon AND the confirm button together,
   * deliberately as one prop rather than two: a `confirmVariant` on its own leaves a red warning
   * triangle sitting above a primary-blue button, which reads as a mistake.
   *
   * Defaults to `danger`, so every existing caller is unchanged.
   *
   * Use `default` for a confirmation that builds rather than destroys — approving, activating,
   * restoring. Three callers were already rendering red buttons for exactly those
   * (GovernanceTab, EntityLifecyclePanel) because this was hardcoded.
   */
  tone?: 'danger' | 'default'
  /**
   * Optional detail rendered between the description and the footer — a callout explaining the
   * consequence, a list of what will be affected. Two callers forked the component for exactly
   * this. NOT for form fields: a confirm that collects input (a reason, a type-to-confirm
   * phrase) is a different component, which is why `PurgeRecordDialog` stays separate.
   */
  children?: ReactNode
}

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description = 'This action cannot be undone.',
  onConfirm,
  isLoading,
  confirmLabel = 'Delete',
  cancelLabel = 'Cancel',
  errorBanner,
  tone = 'danger',
  children,
}: ConfirmDialogProps) {
  const isDanger = tone === 'danger'
  const Icon = isDanger ? AlertTriangle : HelpCircle
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <div className="flex items-start gap-3">
            <div
              className={cn(
                'mt-0.5 grid place-items-center w-8 h-8 rounded-full shrink-0',
                isDanger ? 'bg-danger-subtle text-danger' : 'bg-primary-subtle text-primary',
              )}
            >
              <Icon size={15} aria-hidden="true" />
            </div>
            <div className="min-w-0">
              <DialogTitle>{title}</DialogTitle>
              {description && (
                <DialogDescription className="mt-1">{description}</DialogDescription>
              )}
            </div>
          </div>
        </DialogHeader>
        {children}
        {errorBanner && (
          <div className="rounded-lg bg-danger-subtle border border-danger/20 px-3 py-2">
            <Text variant="body-sm" color="danger">{errorBanner}</Text>
          </div>
        )}
        <DialogFooter className="mt-4 gap-2">
          <DialogClose asChild>
            <Button variant="outline" disabled={isLoading}>
              {cancelLabel}
            </Button>
          </DialogClose>
          <Button variant={isDanger ? 'destructive' : 'default'} onClick={onConfirm} disabled={isLoading}>
            {isLoading ? `${confirmLabel}…` : confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
